<script setup lang="ts">
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import ListingFilters from '@/components/Globals/ListingFilters.vue'
import EnergySensorDetail from '@/components/Sensors/EnergySensorDetail.vue'
import OpenAlarmsInsight from '@/components/Sensors/OpenAlarmsInsight.vue'
import { SensorType, translateSensorType, getSensorColor } from '@/interfaces/enums'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { useSensorStore } from '@/store/sensor'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'
import { useRouter } from 'vue-router'

const openAlarmsInsightRef = ref<InstanceType<typeof OpenAlarmsInsight> | null>(null)
const correlationalAlarmsInsightRef = ref<InstanceType<typeof OpenAlarmsInsight> | null>(null)

const { t } = useI18n()
const isLoading = ref(true)

const store = useSensorStore()
const { sensors } = storeToRefs(store)
const fetchSensors = store.fetch
const router = useRouter()

const sensorName = ref('')
const selectedSensorType = ref<SensorType | ''>('')

const sensorTypes = [
  { value: '', label: 'Todos os tipos' },
  { value: SensorType.ELECTRICITY, label: translateSensorType(SensorType.ELECTRICITY) },
  { value: SensorType.ROTATION, label: translateSensorType(SensorType.ROTATION) },
  { value: SensorType.TEMPERATURE, label: translateSensorType(SensorType.TEMPERATURE) },
  { value: SensorType.DOOR_OPEN, label: translateSensorType(SensorType.DOOR_OPEN) },
]

onMounted(async () => {
  await fetchSensors()
  isLoading.value = false
})

const filteredSensors = computed(() => {
  let filtered = sensors.value

  if (selectedSensorType.value) {
    filtered = filtered.filter((sensor) => sensor.sensor_type === selectedSensorType.value)
  }

  if (sensorName.value.trim()) {
    const q = sensorName.value
    filtered = filtered.filter(
      (sensor) =>
        matchesSearchAccentInsensitive(sensor.name, q) ||
        matchesSearchAccentInsensitive(sensor.mac_address ?? '', q) ||
        matchesSearchAccentInsensitive(sensor.subid.toString(), q),
    )
  }

  return filtered
})

const activeSensors = computed(() => filteredSensors.value.filter((s) => !s.archived))
const archivedSensors = computed(() => filteredSensors.value.filter((s) => s.archived))

// Ordem das seções por tipo de sensor
const sensorTypeOrder: SensorType[] = [
  SensorType.ELECTRICITY,
  SensorType.TEMPERATURE,
  SensorType.DOOR_OPEN,
  SensorType.ROTATION,
]

// Agrupa os sensores ativos por tipo, mantendo a ordem definida e
// ignorando grupos sem sensores
const groupedActiveSensors = computed(() => {
  return sensorTypeOrder
    .map((type) => ({
      type,
      label: translateSensorType(type),
      color: getSensorColor(type),
      sensors: activeSensors.value.filter((s) => s.sensor_type === type),
    }))
    .filter((group) => group.sensors.length > 0)
})

const showArchivedSection = ref(false)

function navigateToSensorDetail(sensor) {
  router.push({ name: 'sensor-detail', params: { id: sensor.id } })
}

function handleRefresh() {
  isLoading.value = true
  Promise.all([
    fetchSensors({ force: true }),
    openAlarmsInsightRef.value?.fetchOpenAlarms() ?? Promise.resolve(),
    correlationalAlarmsInsightRef.value?.fetchOpenAlarms() ?? Promise.resolve(),
  ]).then(() => {
    isLoading.value = false
  })
}
</script>

<template>
  <DefaultLayout>
    <Loader v-if="isLoading" />

    <div v-else class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        :to="{ name: 'dashboard' }"
        :title="t('sensors.name')"
        subtitle="Monitore e acompanhe os dados dos seus sensores em tempo real.">
        <template #actions>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="flex items-center justify-center border border-stroke p-2 text-slate-700 transition hover:bg-slate-50"
              title="Atualizar"
              @click="handleRefresh">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <ListingFilters>
              <template #default="{ close }">
                <input
                  v-model="sensorName"
                  type="text"
                  class="mb-3 w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  :placeholder="t('placeholders.sensors.sensors_mac_or_subid')"
                  @keydown.enter="close" />
                <select
                  v-model="selectedSensorType"
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none">
                  <option v-for="type in sensorTypes" :key="type.value" :value="type.value">
                    {{ type.label }}
                  </option>
                </select>
              </template>
            </ListingFilters>
          </div>
        </template>
      </PageHeader>

      <div class="mb-4 flex flex-col items-start gap-4 lg:flex-row">
        <OpenAlarmsInsight ref="openAlarmsInsightRef" class="min-w-0 flex-1" />
        <OpenAlarmsInsight
          ref="correlationalAlarmsInsightRef"
          is-correlational-alarms
          class="min-w-0 flex-1" />
      </div>

      <template v-if="activeSensors.length > 0">
        <section v-for="group in groupedActiveSensors" :key="group.type" class="mb-8">
          <div class="mb-3 flex items-center gap-2">
            <span :class="`h-4 w-1.5 flex-shrink-0 ${group.color}`" />
            <h2 class="text-base font-semibold text-slate-700">
              {{ group.label }}
            </h2>
            <span class="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-slate-200 px-1.5 text-xs font-medium text-slate-700">
              {{ group.sensors.length }}
            </span>
          </div>
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            <div v-for="sensor in group.sensors" :key="sensor.id">
              <EnergySensorDetail @click="navigateToSensorDetail(sensor)" :sensor="sensor" />
            </div>
          </div>
        </section>
      </template>

      <div v-else-if="archivedSensors.length === 0" class="flex flex-col items-center justify-center bg-white px-6 py-12 shadow-sm">
        <div class="text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 class="mt-4 text-lg font-medium text-gray-900">
            {{ sensorName || selectedSensorType ? 'Nenhum sensor encontrado' : 'Nenhum sensor cadastrado' }}
          </h3>
          <p class="mt-2 text-sm text-gray-500">
            {{ sensorName || selectedSensorType
              ? 'Tente ajustar os filtros para encontrar sensores.'
              : 'Comece adicionando sensores ao sistema.' }}
          </p>
        </div>
      </div>

      <section v-if="archivedSensors.length > 0" class="mt-10">
        <button
          type="button"
          class="flex w-full items-center justify-between border-t border-stroke pt-4 text-left"
          @click="showArchivedSection = !showArchivedSection">
          <div>
            <h2 class="text-base font-semibold text-slate-700">
              Sensores desativados
              <span class="ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-slate-200 px-1.5 text-xs font-medium text-slate-700">
                {{ archivedSensors.length }}
              </span>
            </h2>
            <p class="mt-1 text-xs text-slate-500">
              Sensores cuja análise foi pausada. Alarmes históricos seguem visíveis nos relatórios.
            </p>
          </div>
          <svg class="h-5 w-5 text-slate-500 transition-transform" :class="{ 'rotate-180': showArchivedSection }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div v-show="showArchivedSection" class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          <div v-for="sensor in archivedSensors" :key="sensor.id" class="opacity-70 hover:opacity-100 transition cursor-pointer">
            <EnergySensorDetail @click="navigateToSensorDetail(sensor)" :sensor="sensor" />
          </div>
        </div>
      </section>
    </div>
  </DefaultLayout>
</template>
